"use client"

import { useCallback, useEffect, useState } from "react"
import { ArrowUpRight } from "lucide-react"
import Image from "next/image"
import { apiUrl, authHeaders } from "@/lib/api"
import FetchError from "./fetch-error"

interface StoreObj { id: number; name: string }
interface Produto {
  id: number
  title: string
  store: StoreObj
  discount_percentage?: number
  price_current?: string
  price_original?: string
  image_url?: string
  url?: string
  created_at?: string
}

function formatPrice(val?: string | number): string {
  if (!val) return "—"
  const n = typeof val === "string" ? parseFloat(val) : val
  if (isNaN(n) || n <= 0) return "—"
  return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })
}

function timeAgo(date?: string): string {
  if (!date) return ""
  const min = Math.floor((Date.now() - new Date(date).getTime()) / 60000)
  if (min < 1) return "agora"
  if (min < 60) return `${min}m`
  const h = Math.floor(min / 60)
  if (h < 24) return `${h}h`
  return `${Math.floor(h / 24)}d`
}

export default function RecentPosts() {
  const [produtos, setProdutos] = useState<Produto[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  const load = useCallback(() => {
    const token = typeof window !== "undefined" ? localStorage.getItem("access_token") : null
    fetch(`${apiUrl()}/products/?ordering=-created_at&page_size=5`, { headers: authHeaders(token) })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`)
        return r.json()
      })
      .then((data) => {
        const lista = data.results ?? (Array.isArray(data) ? data : [])
        setProdutos(lista)
        setError(false)
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    load()
  }, [load])

  return (
    <div className="surface-card flex h-full flex-col">
      <div className="flex items-baseline justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Esteira</div>
          <h3 className="mt-1 font-display text-2xl font-normal tracking-tight text-foreground">
            Produtos recentes
          </h3>
        </div>
        <a
          href="/dashboard-cms/produtos"
          className="group inline-flex items-center gap-1 text-xs text-muted-foreground transition-colors hover:text-foreground"
        >
          Ver todos
          <ArrowUpRight className="h-3 w-3 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
        </a>
      </div>

      <div className="mt-5 flex-1">
        {loading ? (
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => <div key={i} className="h-16 animate-pulse rounded bg-muted/30" />)}
          </div>
        ) : error && produtos.length === 0 ? (
          <FetchError what="os produtos" onRetry={load} className="min-h-[280px]" />
        ) : produtos.length === 0 ? (
          <div className="flex h-full min-h-[280px] items-center justify-center text-center">
            <p className="max-w-[280px] text-sm text-muted-foreground">
              Os robôs estão de olho. Nada novo no último ciclo.
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-border/40">
            {produtos.map((p) => {
              const lojaNome = p.store?.name || ""
              const desconto = p.discount_percentage ?? 0
              return (
                <li key={p.id} className="group">
                  <a
                    href={p.url || "#"}
                    target={p.url ? "_blank" : undefined}
                    rel="noopener noreferrer"
                    className="flex items-center gap-4 py-4 -mx-2 px-2 rounded-md transition-colors hover:bg-muted/20"
                  >
                    <div className="relative h-12 w-12 flex-shrink-0 overflow-hidden rounded-md border border-border/40 bg-muted/30">
                      {p.image_url ? (
                        <Image src={p.image_url} alt={p.title} fill className="object-contain p-1" unoptimized />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center text-muted-foreground/40">·</div>
                      )}
                    </div>

                    <div className="min-w-0 flex-1">
                      <h4 className="truncate text-sm font-medium text-foreground">{p.title}</h4>
                      <div className="mt-0.5 flex items-baseline gap-2 text-xs text-muted-foreground">
                        {lojaNome && <span className="text-foreground/70">{lojaNome}</span>}
                        {desconto > 0 && (
                          <>
                            <span>·</span>
                            <span className="font-mono text-[var(--brand-amber)]">−{desconto}%</span>
                          </>
                        )}
                        <span>·</span>
                        <span className="font-mono">{timeAgo(p.created_at)}</span>
                      </div>
                    </div>

                    <div className="flex-shrink-0 text-right">
                      <div className="font-mono text-sm font-semibold text-foreground">{formatPrice(p.price_current)}</div>
                      {p.price_original && parseFloat(p.price_original) > 0 && parseFloat(p.price_original) !== parseFloat(p.price_current || "0") && (
                        <div className="font-mono text-[11px] text-muted-foreground line-through">{formatPrice(p.price_original)}</div>
                      )}
                    </div>

                    <ArrowUpRight className="hidden h-3.5 w-3.5 flex-shrink-0 text-muted-foreground transition-all group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-foreground sm:block" />
                  </a>
                </li>
              )
            })}
          </ul>
        )}
      </div>
    </div>
  )
}
